import { nextRetryAt } from "./retry";
import type { OfflineOperation, OutboxScope, OutboxStore, SyncTransport } from "./types";

export interface SyncClock {
  now(): Date;
}

export interface SyncRunSummary {
  interruptedReset: number;
  attempted: number;
  synced: number;
  retryable: number;
  conflicts: number;
}

const systemClock: SyncClock = {
  now: () => new Date(),
};

function unexpectedErrorCode(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.slice(0, 120);
  }

  return "SYNC_TRANSPORT_ERROR";
}

function newSummary(interruptedReset = 0): SyncRunSummary {
  return {
    interruptedReset,
    attempted: 0,
    synced: 0,
    retryable: 0,
    conflicts: 0,
  };
}

function matchesScope(operation: OfflineOperation, scope: OutboxScope): boolean {
  return operation.farmId === scope.farmId && operation.actorId === scope.actorId;
}

export class SyncEngine {
  constructor(
    private readonly store: OutboxStore,
    private readonly transport: SyncTransport,
    private readonly clock: SyncClock = systemClock,
  ) {}

  async enqueue(operation: OfflineOperation): Promise<void> {
    await this.store.put(operation);
  }

  private async syncOperation(
    operation: OfflineOperation,
    now: Date,
    summary: SyncRunSummary,
  ): Promise<void> {
    summary.attempted += 1;
    await this.store.update(operation.operationId, { status: "syncing" });

    try {
      const result = await this.transport.send(operation);

      if (result.kind === "applied") {
        await this.store.update(operation.operationId, {
          status: "synced",
          ...(result.serverResult ? { serverResult: result.serverResult } : {}),
        });
        summary.synced += 1;
        return;
      }

      if (result.kind === "terminal_conflict") {
        await this.store.update(operation.operationId, {
          status: "terminal_conflict",
          lastErrorCode: result.code,
          ...(result.details ? { conflictDetails: result.details } : {}),
        });
        summary.conflicts += 1;
        return;
      }

      const retryCount = operation.retryCount + 1;
      await this.store.update(operation.operationId, {
        status: "retryable_error",
        retryCount,
        lastErrorCode: result.code,
        nextRetryAt: nextRetryAt(now, retryCount, result.retryAfterMs),
      });
      summary.retryable += 1;
    } catch (error) {
      const retryCount = operation.retryCount + 1;
      await this.store.update(operation.operationId, {
        status: "retryable_error",
        retryCount,
        lastErrorCode: unexpectedErrorCode(error),
        nextRetryAt: nextRetryAt(now, retryCount),
      });
      summary.retryable += 1;
    }
  }

  async run(limit = 50, scope?: OutboxScope): Promise<SyncRunSummary> {
    const interruptedReset = await this.store.resetInterrupted(scope);
    const now = this.clock.now();
    const ready = await this.store.listReady(now.toISOString(), limit, scope);
    const summary = newSummary(interruptedReset);

    for (const operation of ready) {
      await this.syncOperation(operation, now, summary);
    }

    return summary;
  }

  async runOperation(operationId: string, scope: OutboxScope): Promise<SyncRunSummary> {
    const operation = await this.store.get(operationId);

    if (!operation) {
      throw new Error("SYNC_OPERATION_NOT_FOUND");
    }
    if (!matchesScope(operation, scope)) {
      throw new Error("SYNC_OPERATION_SCOPE_MISMATCH");
    }
    if (operation.status !== "pending" && operation.status !== "retryable_error") {
      throw new Error("SYNC_OPERATION_NOT_RETRYABLE");
    }

    const summary = newSummary();
    await this.syncOperation(operation, this.clock.now(), summary);
    return summary;
  }
}
